import React from 'react';

import { Box, Stack, Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';

/**
 * Width of the leading slot, shared by every list that uses `ListRow` (VERA-978).
 *
 * One value, exported, so the Scans avatar, the Activity event icon and both
 * skeletons can never drift apart again — they were 32px and 40px respectively
 * before this component existed.
 *
 * It is a SLOT width, not a disc diameter. The Scans avatar fills it; the Activity
 * icon is an 18px glyph centred in it. What the constant guarantees is that the
 * label column starts at the same x on every row of every list — the alignment
 * rail, not the shape of what sits in it.
 */
export const LIST_ROW_LEADING_SIZE = 40;

/** Vertical padding inside a row, in px. `py: 2` on this app's 8px MUI base. */
export const LIST_ROW_PADDING_Y = 16;

export interface ListRowProps {
  /**
   * Leading visual — an `Avatar`, a bare icon, a logo. Rendered into a fixed
   * {@link LIST_ROW_LEADING_SIZE} box that never shrinks, so the text column starts
   * at the same x on every row of every list regardless of what the caller puts in
   * it. Content narrower than the slot (the Activity glyph) is centred, not
   * collapsed — the column width is what keeps the labels on one rail.
   */
  leading?: React.ReactNode;
  /** Primary line. Wraps by default; pass `truncate` for a single clipped line. */
  label: React.ReactNode;
  /** Secondary line under the label. */
  sublabel?: React.ReactNode;
  /**
   * Render the sublabel in `text.disabled` rather than `text.secondary` — for
   * placeholder sublabels ("—") where the row has nothing real to say, so the
   * list keeps two-line parity without the placeholder reading as content.
   */
  sublabelMuted?: boolean;
  /**
   * Trailing content that sits before the timestamp — a status chip, a badge.
   * Right-aligned, flush to the row's right edge, and never shrinks.
   */
  meta?: React.ReactNode;
  /** Relative timestamp string ("2 days ago"). Styled by this component. */
  time?: string;
  /** Native tooltip on the timestamp, normally the absolute date. */
  timeTitle?: string;
  /**
   * Clip the label/sublabel to one line each instead of wrapping. Use when the row's
   * text is an unbounded value (a name, an email, an address) and two-line parity
   * matters more than seeing the whole string. Leave off when the label comes from a
   * known, finite set of phrases that should be read in full.
   */
  truncate?: boolean;
  /**
   * Whether this row draws its bottom hairline.
   *
   * Omit it and the row falls back to `&:last-of-type`, which is correct ONLY
   * when the rows are siblings. That assumption is silently false wherever a
   * consumer wraps each row individually — the Activity feed wraps every row in
   * its own tap-target `Box`, so each `ListRow` is the only child of its parent,
   * `:last-of-type` matches EVERY row, and the whole list loses its separators.
   *
   * The visible symptom was that `ActivityFeedSkeleton` drew hairlines and the
   * loaded content did not, so the list changed shape as it resolved — a
   * skeleton is supposed to be footprint-identical (working-conventions §19).
   *
   * CSS cannot express "am I last" from inside a wrapper, because a selector
   * cannot look up. So a wrapping consumer states it. Pass
   * `divider={index !== items.length - 1}`.
   */
  divider?: boolean;
}

/**
 * One row of a divider-separated list (VERA-978).
 *
 * Owns the layout every such list shares — the fixed-size circular leading slot, the
 * label/sublabel column, the right-aligned trailing slot, the row padding and the
 * bottom hairline — so callers supply only content. Extracted when the Activity
 * timeline became the second hand-roll of the Scans row and the two had already
 * drifted on avatar size (32 vs 40), padding (16 vs 12) and where the timestamp
 * lived (right column vs inline in the sublabel). Working conventions §36.
 *
 * Rows are meant to sit flush in a `<Stack spacing={0}>`; the hairline is the
 * separator, so an extra gap between rows would leave it floating.
 */
export const ListRow: React.FC<ListRowProps> = ({
  leading,
  label,
  sublabel,
  sublabelMuted = false,
  meta,
  time,
  timeTitle,
  truncate = false,
  divider,
}) => {
  return (
    <Stack
      direction='row'
      alignItems='center'
      // 16px on this app's 8px MUI base. NOT @repo/ui's 4px base — copying a
      // platform `spacing={3}` here would render 24px (working conventions §29).
      spacing={2}
      sx={theme => ({
        py: 2,
        borderBottom:
          divider === false ? 'none' : `1px solid ${theme.palette.grey[200]}`,
        // The sibling fallback, kept for every consumer that does NOT wrap its
        // rows. An explicit `divider` wins over it — see the prop.
        ...(divider === undefined ? { '&:last-of-type': { borderBottom: 'none' } } : {}),
      })}
    >
      {leading !== undefined && leading !== null && (
        <Box
          sx={{
            width: LIST_ROW_LEADING_SIZE,
            height: LIST_ROW_LEADING_SIZE,
            minWidth: LIST_ROW_LEADING_SIZE,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {leading}
        </Box>
      )}

      {/* minWidth:0 is what actually lets the label wrap/ellipsis inside a flex row —
          without it the text column refuses to shrink below its content width and
          pushes the trailing slot off the row. */}
      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 500, color: 'text.primary' }} noWrap={truncate}>
          {label}
        </Typography>
        {sublabel !== undefined && sublabel !== null && (
          <Typography
            variant='body2'
            sx={{ color: sublabelMuted ? 'text.disabled' : 'text.secondary' }}
            noWrap={truncate}
          >
            {sublabel}
          </Typography>
        )}
      </Stack>

      {(meta || time) && (
        <Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
          {meta}
          {time && (
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              title={timeTitle}
            >
              {time}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

/** Glyph size for {@link ListRowIcon}, in px. */
const LIST_ROW_ICON_SIZE = 18;

export interface ListRowIconProps {
  /** Lucide icon component, rendered at 18px. */
  icon: LucideIcon;
}

/**
 * The icon treatment for a `ListRow` leading slot — a BARE glyph, no disc, no
 * border (VERA-978).
 *
 * It fills the full {@link LIST_ROW_LEADING_SIZE} slot and centres the 18px glyph
 * inside it. That fixed width is the whole point and is not an accident of the
 * wrapper: the slot, not the glyph, is what keeps every label on the same rail down
 * the list. Shrink this to the glyph's intrinsic width and the Activity labels stop
 * lining up with each other and with the Scans list, which passes a 40px `Avatar`
 * into the same slot.
 *
 * Used ONLY by the Activity timeline. The Scans list keeps its circular `Avatar`
 * (photo → initials), which is a real avatar rather than an icon treatment, so the
 * disc there carries meaning this one did not.
 *
 * Was `ListRowIconCircle`, a grey disc with a hairline border, briefly extended
 * with a warning/danger tint for exception events. Both the tint and the disc were
 * rejected on review: the tint made the timeline shout, and the disc was chrome
 * around a glyph that reads perfectly well on its own. Renamed rather than left as
 * an `IconCircle` that draws no circle.
 */
export const ListRowIcon: React.FC<ListRowIconProps> = ({ icon: Icon }) => (
  <Box
    sx={theme => ({
      width: LIST_ROW_LEADING_SIZE,
      height: LIST_ROW_LEADING_SIZE,
      // Lucide glyphs draw in `currentColor`, so set it here rather than passing a
      // `color` prop at every call site. Explicit rather than inherited so the glyph
      // can't drift with whatever an ancestor happens to set.
      color: theme.palette.text.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    })}
  >
    <Icon size={LIST_ROW_ICON_SIZE} />
  </Box>
);

export default ListRow;
